/**
 * Backend API Client
 * 
 * Centralized client for communicating with the Cache backend API.
 * Handles product analysis, pricing, and job status tracking.
 */

import Constants from 'expo-constants';
import { normalizeCondition } from './gemini';
import { supabase } from './supabase';
import type { AnalyzedItem } from './gemini';
import type { VariantMixEvidence } from './variantMixAnnotation';
import type { IdentityOutcome } from './itemIdentity';

// Get backend configuration from environment
const BACKEND_API_URL = Constants.expoConfig?.extra?.EXPO_PUBLIC_BACKEND_API_URL;
const API_SECRET = Constants.expoConfig?.extra?.EXPO_PUBLIC_BACKEND_API_SECRET;

console.log('[BackendAPI] Configuration loaded:', {
  hasUrl: !!BACKEND_API_URL,
  url: BACKEND_API_URL || 'NOT SET',
  hasSecret: !!API_SECRET,
  secretLength: API_SECRET?.length || 0,
});

if (!BACKEND_API_URL) {
  console.warn('⚠️ BACKEND_API_URL not configured in app.config.ts - Backend API calls will fail!');
}

if (!API_SECRET) {
  // Add Item analysis authenticates as the signed-in user and does not need it.
  console.warn('⚠️ BACKEND_API_SECRET not configured in app.config.ts - job/market-stats calls will fail!');
}

/**
 * Pricing data structure with source tracking
 */
export interface PriceData {
  source: 'market_data' | 'gemini_estimate' | 'none';
  
  // For market_data source
  by_condition?: {
    [condition: string]: {
      min: number;
      median: number;
      max: number;
      mean: number;
      count: number;
      snapshot_date: string;
    };
  };
  last_updated?: string;
  data_points?: number;
  
  // For gemini_estimate source
  estimated_value?: number;
  note?: string;
}

/**
 * Product analysis response from backend
 */
export interface ProductAnalysisResponse {
  success: boolean;
  message: string;

  // Product identification
  product_name: string | null;
  brand: string | null;
  model: string | null;
  family: string | null;
  sku: string | null;
  confidence: number | null;

  // Database IDs
  instrument_id: number | null;
  variant_id: string | null;  // Supabase UUID
  brand_id: string | null;    // Supabase UUID
  family_id: string | null;   // Supabase UUID
  model_id: string | null;    // Supabase UUID

  // Gemini analysis fields (for frontend compatibility)
  description?: string | null;
  category?: string | null;
  condition?: string | null;
  condition_score?: number | null;
  demand_score?: number | null;
  missing_info?: string[] | null;
  purchase_date?: string | null;
  original_value?: number | null;
  depreciation_rate?: number | null;

  // Pricing data
  price_data: PriceData;

  // Aggregated valuation (what will be saved to user_products)
  aggregated_valuation?: number | null;
  review_valuation?: number | null;
  review_valuation_min?: number | null;
  review_valuation_max?: number | null;
  review_valuation_basis?: string | null;
  review_valuation_evidence_count?: number | null;

  // Background job tracking
  price_discovery_job_id: number | null;
  price_discovery_status: 'complete' | 'pending' | 'not_needed';

  // Full Gemini response for debugging
  gemini_response?: Record<string, any>;

  // Storage path (returned after backend uploads base64 images)
  storage_path?: string | string[] | null;

  // Server-generated UUID — set by the multi-product video path, where each
  // detected product gets its own server-side UUID. None for single-product
  // calls that still pass product_id in the request.
  product_id?: string | null;

  // T1 identity outcome; absent when the backend's scan identity resolver flag is off.
  identity?: IdentityOutcome | null;
}

/**
 * Multi-product analysis response — returned by all `/run-end-to-end` calls.
 * For single-image / gallery flows the `products` array contains exactly one
 * entry. For video flows it can contain up to MAX_PRODUCTS_PER_VIDEO (10).
 */
export interface MultiProductAnalysisResponse {
  success: boolean;
  message: string;
  products: ProductAnalysisResponse[];
  is_video: boolean;
  storage_path?: string | string[] | null;
}

/**
 * Job status response
 */
export interface JobStatusResponse {
  id: number;
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  last_error: string | null;
  payload: any;
}

/**
 * Market stats response
 */
export interface MarketStatsResponse {
  [condition: string]: {
    min: number;
    median: number;
    max: number;
    mean: number;
    count: number;
    snapshot_date: string;
  };
}

/**
 * Internal helper — POSTs to /run-end-to-end and returns the multi-product envelope.
 * Used by both `analyzeProductImage` (which unwraps to a single product) and
 * `analyzeProductVideo` (which returns the array directly).
 *
 * Authenticates as the signed-in user (Supabase access token in `X-User-Token`); the backend
 * verifies it and treats that user as the request's owner. No shared secret is sent, so this
 * works on builds that do not bundle one (web).
 */
async function runEndToEnd(requestBody: Record<string, unknown>): Promise<MultiProductAnalysisResponse> {
  if (!BACKEND_API_URL) {
    throw new Error('Backend API URL not configured');
  }
  const { data } = await supabase.auth.getSession();
  const accessToken = data.session?.access_token;
  if (!accessToken) {
    throw new Error('You must be signed in to analyze items');
  }

  const response = await fetch(`${BACKEND_API_URL}/run-end-to-end`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-User-Token': accessToken,
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('[BackendAPI] Error response:', errorText);
    throw new Error(`Backend API error: ${response.status} ${response.statusText}`);
  }

  return await response.json() as MultiProductAnalysisResponse;
}

/**
 * Analyze a single product (single image or multi-angle photos of the SAME item).
 *
 * For video uploads that may contain multiple distinct products, use
 * `analyzeProductVideo` instead.
 *
 * @param imageSource - Either a storage path / paths OR base64 encoded image(s)
 * @param productId - Unique product ID for tracking
 * @param forcePriceDiscovery - Force price discovery even if product exists
 * @param userId - User ID for Supabase user_products table
 * @param isVideo - Legacy flag, retained for API compatibility (ignored for multi-product detection)
 * @param isBase64 - If true, imageSource is base64 encoded
 * @returns Product analysis with pricing data and storage_path
 */
export async function analyzeProductImage(
  imageSource: string | string[],
  productId: string,
  forcePriceDiscovery: boolean = false,
  userId?: string,
  isVideo: boolean = false,
  isBase64: boolean = false
): Promise<ProductAnalysisResponse> {
  const isMultiImage = Array.isArray(imageSource);
  console.log('[BackendAPI] Analyzing product:', {
    imageSource: isBase64
      ? `base64 (${isMultiImage ? `${imageSource.length} images` : '1 image'})`
      : (isMultiImage ? `${imageSource.length} images` : imageSource),
    productId,
    forcePriceDiscovery,
    userId,
    isVideo,
    isMultiImage,
    isBase64,
  });

  const requestBody = isBase64
    ? {
        image_base64: imageSource,
        product_id: productId,
        force_price: forcePriceDiscovery,
        user_id: userId,
        is_video: isVideo,
      }
    : {
        image_path: imageSource,
        product_id: productId,
        force_price: forcePriceDiscovery,
        user_id: userId,
        is_video: isVideo,
      };

  try {
    const envelope = await runEndToEnd(requestBody);

    if (!Array.isArray(envelope.products)) {
      console.error(
        '[BackendAPI] Photo response missing `products` array:',
        envelope,
      );
      throw new Error('Backend returned unexpected response shape (missing products array)');
    }

    // Single-product callers always expect one entry. The backend wraps single
    // flows in a one-element array; if it ever returns zero, surface a failure
    // response so callers' `success` checks behave as before.
    if (envelope.products.length === 0) {
      return {
        success: false,
        message: envelope.message || 'No product identified',
        product_name: null,
        brand: null,
        model: null,
        family: null,
        sku: null,
        confidence: null,
        instrument_id: null,
        variant_id: null,
        brand_id: null,
        family_id: null,
        model_id: null,
        price_data: { source: 'none' },
        price_discovery_job_id: null,
        price_discovery_status: 'not_needed',
        storage_path: envelope.storage_path,
      };
    }

    const data = envelope.products[0];
    console.log('[BackendAPI] Analysis complete:', {
      success: data.success,
      product: data.product_name,
      priceSource: data.price_data.source,
      status: data.price_discovery_status,
      storagePath: data.storage_path,
    });
    return data;
  } catch (error) {
    console.error('[BackendAPI] Failed to analyze product:', error);
    throw error;
  }
}

/**
 * Analyze a video for one or more distinct products.
 *
 * Sends base64-encoded video frames to the backend, which runs multi-product
 * detection via Gemini and returns one entry per distinct product (after
 * dedupe on brand+model). Up to MAX_PRODUCTS_PER_VIDEO (10) products are
 * returned. Per-product failures are surfaced as entries with success=false
 * rather than failing the whole request.
 */
export async function analyzeProductVideo(
  frameBase64Images: string[],
  productId: string,
  forcePriceDiscovery: boolean = false,
  userId?: string,
): Promise<MultiProductAnalysisResponse> {
  console.log('[BackendAPI] Analyzing video:', {
    frameCount: frameBase64Images.length,
    productId,
    forcePriceDiscovery,
    userId,
  });

  const requestBody = {
    image_base64: frameBase64Images,
    product_id: productId,
    force_price: forcePriceDiscovery,
    user_id: userId,
    is_video: true,
  };

  try {
    const envelope = await runEndToEnd(requestBody);
    if (!Array.isArray(envelope.products)) {
      console.error(
        '[BackendAPI] Video response missing `products` array:',
        envelope,
      );
    }
    console.log('[BackendAPI] Video analysis complete:', {
      success: envelope.success,
      productCount: envelope.products?.length ?? 0,
      storagePath: envelope.storage_path,
    });
    return envelope;
  } catch (error) {
    console.error('[BackendAPI] Failed to analyze video:', error);
    throw error;
  }
}

/**
 * Get job status by ID
 * 
 * @param jobId - Background job ID
 * @returns Job status and details
 */
export async function getJobStatus(jobId: number): Promise<JobStatusResponse> {
  if (!BACKEND_API_URL) {
    throw new Error('Backend API URL not configured');
  }
  
  if (!API_SECRET) {
    throw new Error('Backend API secret not configured');
  }
  
  try {
    const response = await fetch(`${BACKEND_API_URL}/jobs/${jobId}`, {
      method: 'GET',
      headers: {
        'X-API-Secret': API_SECRET,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to get job status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('[BackendAPI] Failed to get job status:', error);
    throw error;
  }
}

/**
 * Get latest market stats for an instrument
 * 
 * @param instrumentId - Instrument ID
 * @returns Market statistics by condition
 */
export async function getMarketStats(instrumentId: number): Promise<MarketStatsResponse> {
  if (!BACKEND_API_URL) {
    throw new Error('Backend API URL not configured');
  }
  
  if (!API_SECRET) {
    throw new Error('Backend API secret not configured');
  }
  
  try {
    const response = await fetch(`${BACKEND_API_URL}/market-stats/${instrumentId}`, {
      method: 'GET',
      headers: {
        'X-API-Secret': API_SECRET,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to get market stats: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('[BackendAPI] Failed to get market stats:', error);
    throw error;
  }
}

/**
 * Helper to extract price range from analysis response
 * 
 * @param analysisResponse - Product analysis response
 * @param preferredCondition - Preferred condition for pricing (default: 'Used')
 * @returns Price range object with median as primary value, or null
 */
export function extractPriceRange(
  analysisResponse: ProductAnalysisResponse,
  preferredCondition: string = 'Used'
): { min: number; max: number; median: number; source: string; dataPoints?: number } | null {
  const { price_data } = analysisResponse;
  
  if (price_data.source === 'market_data' && price_data.by_condition) {
    // Try preferred condition first
    const conditionStats = price_data.by_condition[preferredCondition] || 
                          price_data.by_condition['Used'] ||
                          Object.values(price_data.by_condition)[0];
    
    if (conditionStats) {
      return {
        min: conditionStats.min,
        max: conditionStats.max,
        median: conditionStats.median, // Use median as the primary estimate
        source: 'market',
        dataPoints: conditionStats.count,
      };
    }
  }
  
  if (price_data.source === 'gemini_estimate' && price_data.estimated_value) {
    // Create range from estimate (±20%)
    const estimate = price_data.estimated_value;
    return {
      min: Math.round(estimate * 0.8),
      max: Math.round(estimate * 1.2),
      median: estimate, // Use estimate as median
      source: 'estimate',
    };
  }
  
  return null;
}

// Mirrors restoration_item_market(): a qualified Used-market quote only values a used grade.
const MARKET_QUOTE_CONDITIONS: ReadonlySet<string> = new Set(['Excellent', 'Good', 'Fair', 'Poor', 'Used']);

/**
 * Convert a backend ProductAnalysisResponse into a frontend AnalyzedItem.
 *
 * This is the single canonical mapping between the two shapes — every call site
 * that previously hand-built an AnalyzedItem from a backend response should use
 * this helper instead.
 */
export function toAnalyzedItem(backendResponse: ProductAnalysisResponse): AnalyzedItem {
  const condition = normalizeCondition(backendResponse.condition) || 'Good';
  const market = MARKET_QUOTE_CONDITIONS.has(condition)
    && backendResponse.review_valuation_basis === 'qualified_exact_median'
    && (backendResponse.review_valuation_evidence_count ?? 0) >= 5
    && Number.isFinite(backendResponse.review_valuation) && (backendResponse.review_valuation ?? 0) > 0;
  // source 'none' is the backend's explicit no-price decision (e.g. a T1 protected miss),
  // even when gemini_response still carries the provider's raw estimate.
  const rawEstimate = backendResponse.price_data.source === 'none' ? null
    : backendResponse.price_data.estimated_value ?? backendResponse.gemini_response?.estimated_resale_value_usd;
  const estimate = typeof rawEstimate === 'number' && Number.isFinite(rawEstimate) && rawEstimate > 0
    ? rawEstimate : null;
  const displayValue = market ? backendResponse.review_valuation! : estimate;
  const priceRange = market ? {
    min: backendResponse.review_valuation_min ?? displayValue!,
    max: backendResponse.review_valuation_max ?? displayValue!,
  } : estimate !== null ? { min: Math.round(estimate * 0.8), max: Math.round(estimate * 1.2) } : null;

  return {
    name: backendResponse.product_name || 'Unknown Item',
    brand: backendResponse.brand || undefined,
    model: backendResponse.model || undefined,
    family: backendResponse.family || undefined,
    description: backendResponse.description
      || `${backendResponse.brand || ''} ${backendResponse.model || ''}`.trim()
      || undefined,
    category: backendResponse.category || undefined,
    condition,
    conditionScore: backendResponse.condition_score || undefined,
    demandScore: backendResponse.demand_score || undefined,
    missingInfo: backendResponse.missing_info || undefined,
    purchaseDate: backendResponse.purchase_date || undefined,
    originalValue: backendResponse.original_value || undefined,
    depreciationRate: backendResponse.depreciation_rate || undefined,
    variantFields: backendResponse.gemini_response?.variant_fields || undefined,
    priceRange,
    estimatedValue: displayValue,
    selected: true,
    image_url: '',
    confidenceScore: backendResponse.confidence || 0,
    _backendData: {
      instrument_id: backendResponse.instrument_id,
      variant_id: backendResponse.variant_id,
      brand_id: backendResponse.brand_id,
      family_id: backendResponse.family_id,
      model_id: backendResponse.model_id,
      sku: backendResponse.sku,
      price_source: backendResponse.price_data.source,
      valuation_source: market ? 'market_data' : estimate !== null ? 'gemini_estimate' : null,
      gemini_estimate: estimate,
      price_discovery_status: backendResponse.price_discovery_status,
      price_discovery_job_id: backendResponse.price_discovery_job_id,
      identity: backendResponse.identity ?? null,
    },
  };
}

// ============================================================================
// ADMIN API FUNCTIONS
// ============================================================================

/**
 * Admin pricing overview response
 */
export interface AdminPricingOverview {
  total_products: number;
  products_with_variant: number;
  products_with_market_data: number;
  products_with_stale_data: number;
  products_missing_instrument: number;
  products_missing_market_stats: number;
  products_with_sync_issues: number;
  coverage_percentage: number;
  average_data_age_days: number | null;
}

/**
 * Admin product pricing issue
 */
export interface AdminProductPricingIssue {
  product_id: string;
  product_name: string | null;
  issue_category: string;
  issue_description: string;
  recommended_action: string;
  days_since_update: number | null;
  current_price: number | null;
  instrument_exists: boolean;
  variant_linked: boolean;
  market_data_count: number;
  used_market_data_count: number;
  product_key: string | null;
  instrument_id: number | null;
}

/**
 * Get admin pricing diagnostics overview
 * Requires admin authentication
 * 
 * @param accessToken - Supabase JWT access token
 * @returns Pricing overview statistics
 */
export async function getAdminPricingOverview(
  accessToken: string
): Promise<AdminPricingOverview> {
  if (!BACKEND_API_URL) {
    throw new Error('Backend API URL not configured');
  }
  
  try {
    const response = await fetch(`${BACKEND_API_URL}/admin/diagnostics/overview`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('[BackendAPI] Admin API error:', errorText);
      
      if (response.status === 403) {
        throw new Error('Admin access required');
      }
      
      throw new Error(`Failed to get pricing overview: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('[BackendAPI] Failed to get admin pricing overview:', error);
    throw error;
  }
}

/**
 * Get products without prices (admin diagnostics)
 * Requires admin authentication
 * 
 * @param accessToken - Supabase JWT access token
 * @param limit - Maximum number of products to return
 * @returns List of products with pricing issues
 */
export async function getAdminProductsWithoutPrices(
  accessToken: string,
  limit: number = 200
): Promise<AdminProductPricingIssue[]> {
  if (!BACKEND_API_URL) {
    throw new Error('Backend API URL not configured');
  }
  
  try {
    const response = await fetch(
      `${BACKEND_API_URL}/admin/diagnostics/products-without-prices?limit=${limit}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      }
    );
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('[BackendAPI] Admin API error:', errorText);
      
      if (response.status === 403) {
        throw new Error('Admin access required');
      }
      
      throw new Error(`Failed to get products without prices: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('[BackendAPI] Failed to get products without prices:', error);
    throw error;
  }
}

/**
 * Refresh pricing for a single instrument (admin action)
 * Requires admin authentication
 * 
 * @param accessToken - Supabase JWT access token
 * @param productKey - Product key to refresh
 * @returns Success response
 */
export async function refreshInstrumentPricing(
  accessToken: string,
  productKey: string
): Promise<{ success: boolean; message: string; product_key: string }> {
  if (!BACKEND_API_URL) {
    throw new Error('Backend API URL not configured');
  }
  
  try {
    const response = await fetch(
      `${BACKEND_API_URL}/admin/refresh-instrument/${productKey}`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      }
    );
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('[BackendAPI] Admin API error:', errorText);
      
      if (response.status === 403) {
        throw new Error('Admin access required');
      }
      
      throw new Error(`Failed to refresh pricing: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('[BackendAPI] Failed to refresh instrument pricing:', error);
    throw error;
  }
}

/**
 * Admin comp listing from market_data
 */
export interface AdminCompListing {
  title: string | null;
  price: number;
  condition: string | null;
  source: string;
  item_web_url: string | null;
  used_in_pricing: boolean;
}

/**
 * Per-source summary of comp listings
 */
export interface AdminCompsSummary {
  source: string;
  count: number;
  median_price: number | null;
}

/**
 * Admin comps response
 */
export interface AdminPricingFilter {
  condition: string;
  confidence_threshold: number | null;
  price_min: number | null;
  price_max: number | null;
  included_count: number;
}

export interface AdminCompsResponse {
  instrument_id: number | null;
  total_count: number;
  snapshot_date: string | null;
  summaries: AdminCompsSummary[];
  listings: AdminCompListing[];
  pricing_filter: AdminPricingFilter | null;
}

/**
 * Get comparable marketplace listings for a variant (admin)
 * Requires admin authentication
 *
 * @param accessToken - Supabase JWT access token
 * @param variantId - Variant UUID
 * @returns Comps response with listings and summaries
 */
export async function getAdminComps(
  accessToken: string,
  variantId: string
): Promise<AdminCompsResponse> {
  if (!BACKEND_API_URL) {
    throw new Error('Backend API URL not configured');
  }

  try {
    const response = await fetch(
      `${BACKEND_API_URL}/admin/comps/${variantId}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[BackendAPI] Admin API error:', errorText);

      if (response.status === 403) {
        throw new Error('Admin access required');
      }

      throw new Error(`Failed to get comps: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('[BackendAPI] Failed to get admin comps:', error);
    throw error;
  }
}

// ---------------------------------------------------------------------------
// Catalog Buy-feed + listing page (Back Market catalog import, plan T12)
//
// Read models for the imported catalog. The backend owns the only read path
// for the service-role-only catalog tables (colors / product_specs /
// product_images / category_axis_roles), so these go through the API — never a
// raw `supabase.from(...)` against those tables. Shapes mirror the T11 pydantic
// models in `modules/catalog_read.py` exactly.
//
// Auth note: the catalog is a public end-user read. The backend skips auth for
// GET `/catalog/*` (api_app `validate_api_secret_or_skip_special_routes`), so these
// calls are anon — we intentionally do NOT send `X-API-Secret`, since a per-request
// secret header would defeat the edge/CDN caching the read path is designed for.
// ---------------------------------------------------------------------------

/** A colour option for a listing/variant. `hex` is NULL until a hex backfill. */
export interface CatalogColorSwatch {
  name: string;
  hex: string | null;
}

/**
 * How a variant's headline price was derived — mirrors the backend `PriceBasis`
 * (modules/catalog_read.py). `identity_collapsed` (Phase 3 S1): the variant spans
 * several exact pricing identities, so it has no price and no valid price history.
 * `cosmetic_sibling` (catalog launch T10): a colour-only sibling showing the price of
 * a priced variant on the same card; it has no price history of its own.
 */
export type CatalogPriceBasis =
  | 'trusted_anchor'
  | 'market_median'
  | 'estimate'
  | 'identity_collapsed'
  | 'cosmetic_sibling';

/** One day of a variant's strict-Used 30-day price series (for the chart). */
export interface CatalogPriceHistoryPoint {
  date: string;
  avg_price: number;
}

/** One Buy-feed card (one imported listing). */
export interface CatalogListingCard {
  listing_id: string;
  listing_title: string;
  brand: string;
  category: string | null;
  // Raw catalog identity, surfaced per card for spot-checking on the admin-only feed
  // (a mis-assigned family is then visible/filterable without opening each listing).
  // `family` is the raw product_families.name — often low-quality for long-tail
  // categories; `*_id` are the uuids.
  category_id: string | null;
  family_id: string | null;
  family: string | null;
  hero_image_url: string | null;
  color_swatches: CatalogColorSwatch[];
  starting_at_price_usd: number | null;
  new_price_usd: number | null;
  // 30-day delta of the representative (lowest-priced) variant — feed sparkline /
  // "biggest mover". Null unless that variant has ≥2 points in the window (F2).
  price_delta_30d_pct: number | null;
  // Sum of real held (user_products owned/active) across the listing's variants (F2).
  held_count: number;
}

/** A page of Buy-feed cards with an opaque keyset cursor. */
export interface CatalogListingsPage {
  listings: CatalogListingCard[];
  next_cursor: string | null;
}

/** The listing header carried on a listing-page response. */
export interface CatalogListingSummary {
  listing_id: string;
  listing_title: string;
  brand: string;
  category: string | null;
  family: string | null;
  hero_image_url: string | null;
  starting_at_price_usd: number | null;
  new_price_usd: number | null;
}

/** One in-listing variant (a precise axis combo). */
export interface CatalogVariantDetail {
  variant_id: string;
  attributes: Record<string, unknown>;
  color: CatalogColorSwatch | null;
  specs: Record<string, unknown>;
  price_usd: number | null;
  hero_image_url: string | null;
  // Market block (F2) — null/empty/0 for an unlinked (unpriced) variant.
  price_basis: CatalogPriceBasis | null;
  identity_mixed: boolean | null;
  mixed_axes: string[] | null;
  variant_mixed_evidence: VariantMixEvidence | null;
  comps_tracked: number | null;
  last_price_refresh_at: string | null;
  held_count: number;
  for_sale_count: number; // SYNTHETIC (backend §Non-goals)
  price_history: CatalogPriceHistoryPoint[];
}

/** An in-listing selector (colour / storage / …) derived from category_axis_roles. */
export interface CatalogSelector {
  axis_key: string;
  label: string;
  values: string[];
}

/** A full listing page: header + variants + selectors. */
export interface CatalogListingDetail {
  listing: CatalogListingSummary;
  variants: CatalogVariantDetail[];
  selectors: CatalogSelector[];
}

export interface CatalogListingsQuery {
  category?: string;
  brand?: string;
  // Raw catalog family name (case-insensitive exact match). A catalog-debugging filter
  // on the admin-only feed — pairs with the `family` surfaced per card.
  family?: string;
  // Free-text search over listing title + brand name (case-insensitive substring).
  q?: string;
  cursor?: string;
  limit?: number;
}

function catalogHeaders(): Record<string, string> {
  // GET `/catalog/*` is anon on the backend (auth skip-list) and the read path is meant to
  // be edge/CDN-cacheable, so we deliberately omit `X-API-Secret` (a per-request secret
  // makes responses uncacheable). We also send only `Accept` — not `Content-Type` — because
  // these are body-less GETs and a `Content-Type: application/json` header is not CORS-
  // safelisted, so it would force an OPTIONS preflight round-trip on web builds.
  return { Accept: 'application/json' };
}

/**
 * Fetch one page of Buy-feed cards from the imported catalog.
 * Keyset-paginated: pass the prior page's `next_cursor` to advance.
 */
export async function getCatalogListings(
  query: CatalogListingsQuery = {}
): Promise<CatalogListingsPage> {
  if (!BACKEND_API_URL) {
    throw new Error('Backend API URL not configured');
  }

  const params = new URLSearchParams();
  if (query.category) params.set('category', query.category);
  if (query.brand) params.set('brand', query.brand);
  if (query.family) params.set('family', query.family);
  if (query.q) params.set('q', query.q);
  if (query.cursor) params.set('cursor', query.cursor);
  if (query.limit != null) params.set('limit', String(query.limit));
  const qs = params.toString();

  const response = await fetch(
    `${BACKEND_API_URL}/catalog/listings${qs ? `?${qs}` : ''}`,
    { method: 'GET', headers: catalogHeaders() }
  );

  if (!response.ok) {
    const errorText = await response.text();
    console.error('[BackendAPI] Catalog listings error:', errorText);
    throw new Error(`Failed to get catalog listings: ${response.status}`);
  }

  return await response.json();
}

/**
 * Fetch a single listing page (variants + selectors) by listing id.
 * Returns null on a 404 (unknown / non-imported / malformed id) so callers can
 * degrade gracefully instead of treating "not in catalog" as an error.
 */
export async function getCatalogListing(
  listingId: string
): Promise<CatalogListingDetail | null> {
  if (!BACKEND_API_URL) {
    throw new Error('Backend API URL not configured');
  }

  const response = await fetch(
    `${BACKEND_API_URL}/catalog/listings/${listingId}`,
    { method: 'GET', headers: catalogHeaders() }
  );

  if (response.status === 404) return null;

  if (!response.ok) {
    const errorText = await response.text();
    console.error('[BackendAPI] Catalog listing error:', errorText);
    throw new Error(`Failed to get catalog listing: ${response.status}`);
  }

  return await response.json();
}
